/**
 * Panel 2 — `Appointment Matrix Table` (§A5, Matrix 1 + Matrix 2).
 *
 * Left block: each official's appointments per role. Right block: how many matches they have
 * shared with each other official, diagonal `×`. Both are derived from the fixture list above
 * on every render, which is why the TD can move a name and watch this table answer.
 *
 * The heat shading is the point of the right-hand block: §A4 rule 8 has no hard warning for
 * colleague rotation, so a pair of officials drifting into each other's company repeatedly is
 * something the TD must *see* rather than be told. Same thresholds as Teams Coverage, so the
 * two blocks read the same way.
 */

import type { MatrixOfficial, MatrixTable, RoleFilter, CompetitionFilter } from "./derive.js";
import { competitionLabel, heatClass } from "./derive.js";
import { COMPETITION_TABS, ROLE_TABS, Tabs } from "./Tabs.js";
import { cellClass } from "./useCellFlash.js";

export interface MatrixPanelProps {
  table: MatrixTable;
  competition: CompetitionFilter;
  onCompetition: (value: CompetitionFilter) => void;
  role: RoleFilter;
  onRole: (value: RoleFilter) => void;
  flashing: ReadonlySet<string>;
  /**
   * RUN 37 — what the counts are counted OVER, in words, for the footnote.
   *
   * The table used to be one day's, and its footnote said so. It is now the whole schedule's,
   * because a Technical Delegate ran `Draft The Pool Stage`, saw ten days written, and read a
   * footnote telling him six matches had been reflected. The span is passed in rather than
   * inferred here: this panel counts what it is handed and must never be the second opinion on
   * what that was. The default is the old wording, so a caller that still hands it one day
   * still says `on this day`.
   */
  span?: string;
}

function stub(official: MatrixOfficial): React.JSX.Element {
  return (
    <>
      <span className="rolechip">{official.role === "TO" ? "TO" : official.role === "ATD" ? "ATD" : "JG"}</span>
      {official.surname}
      <span className="nat num">{official.country}</span>
    </>
  );
}

export function MatrixPanel({
  table,
  competition,
  onCompetition,
  role,
  onRole,
  flashing,
  span = "on this day",
}: MatrixPanelProps): React.JSX.Element {
  const shown = table.rows;

  return (
    <section className="panel panel--d2" aria-labelledby="matTitle" data-howto="matrix-panel">
      <div className="panel__head">
        <h3 className="panel__title" id="matTitle">
          Appointment Matrix Table
        </h3>
        <span className="panel__note">Recomputed On Every Edit — Your Balance Check While Drafting</span>
        <span className="spacer" />
        <span className="filtlab">Competition</span>
        <Tabs
          label="Matrix competition filter"
          options={COMPETITION_TABS}
          value={competition}
          onChange={onCompetition}
          tint="competition"
        />
        <span className="filtlab">Officials</span>
        <Tabs label="Matrix officials filter" options={ROLE_TABS} value={role} onChange={onRole} />
      </div>

      <div className="matwrap">
        <table
          className="mattable"
          aria-label="Appointment matrix — per-role counts and co-appointment counts"
        >
          <thead>
            <tr className="matgrp">
              <th scope="col">Officials</th>
              <th scope="col" colSpan={4}>
                Appointments By Role
              </th>
              <th scope="col" colSpan={shown.length + 1} className="g-right">
                Appointed Together With
              </th>
            </tr>
            <tr>
              <th scope="col" className="c-name">
                Official
              </th>
              <th scope="col">TO</th>
              <th scope="col">SJ</th>
              <th scope="col">TJ</th>
              <th scope="col" className="c-tot">
                Total
              </th>
              {shown.map((row, index) => (
                <th
                  key={row.official.tmsPersonId}
                  scope="col"
                  className={index === 0 ? "first-right" : undefined}
                  title={`${row.official.mediaName} (${row.official.country})`}
                >
                  {row.official.surname}
                </th>
              ))}
              <th scope="col" className="c-tot">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {shown.map((row) => {
              const id = row.official.tmsPersonId;
              return (
                <tr
                  key={id}
                  className={row.official.role === "TO" ? "is-to" : undefined}
                  data-testid={`matrix-${id}`}
                >
                  <td
                    className={`c-name${row.official.gender === null ? "" : ` g-${row.official.gender}`}`}
                  >
                    {stub(row.official)}
                  </td>
                  <td className={cellClass(row.roles[0] === 0 ? "c-zero" : "", `m:${id}:TO`, flashing)}>
                    {row.roles[0]}
                  </td>
                  <td className={cellClass(row.roles[1] === 0 ? "c-zero" : "", `m:${id}:SJ`, flashing)}>
                    {row.roles[1]}
                  </td>
                  <td className={cellClass(row.roles[2] === 0 ? "c-zero" : "", `m:${id}:TJ`, flashing)}>
                    {row.roles[2]}
                  </td>
                  <td className={cellClass("c-tot", `m:${id}:rt`, flashing)}>{row.roleTotal}</td>

                  {shown.map((other, index) => {
                    const edge = index === 0 ? " first-right" : "";
                    if (other.official.tmsPersonId === id) {
                      return (
                        <td key={other.official.tmsPersonId} className={`c-diag${edge}`}>
                          ×
                        </td>
                      );
                    }
                    const value = row.co[other.official.tmsPersonId] ?? 0;
                    return (
                      <td
                        key={other.official.tmsPersonId}
                        className={cellClass(
                          `${heatClass(value)}${edge}`,
                          `m:${id}:${other.official.tmsPersonId}`,
                          flashing,
                        )}
                      >
                        {value}
                      </td>
                    );
                  })}

                  <td className={cellClass("c-tot", `m:${id}:cot`, flashing)}>{row.coTotal}</td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td className="c-name">Total</td>
              <td>{table.totals.to}</td>
              <td>{table.totals.sj}</td>
              <td>{table.totals.tj}</td>
              <td>{table.totals.roleTotal}</td>
              {shown.map((row, index) => (
                <td
                  key={row.official.tmsPersonId}
                  className={index === 0 ? "first-right" : undefined}
                >
                  {table.coColumnTotals[row.official.tmsPersonId] ?? 0}
                </td>
              ))}
              <td>{table.totals.coTotal}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="panel__foot">
        <div className="matlegend" aria-label="Co-appointment heat key">
          <span>Co-Appointment Density</span>
          <span className="hsw">
            <i className="k0" aria-hidden="true" />0
          </span>
          <span className="hsw">
            <i className="k1" aria-hidden="true" />1
          </span>
          <span className="hsw">
            <i className="k2" aria-hidden="true" />2
          </span>
          <span className="hsw">
            <i className="k3" aria-hidden="true" />
            3+
          </span>
        </div>
        <b>Reading the matrix —</b> the left block counts each official&apos;s appointments per
        role; the right block counts how many matches they have shared with each other official
        (diagonal = ×). Every figure is computed live from the fixture list above, so the
        competition filter reshapes the whole table.{" "}
        <span data-testid="matrix-scope">
          Currently reflecting <b>{table.fixtures}</b>{" "}
          {competition === "all" ? "" : `${competitionLabel(competition)} `}
          {table.fixtures === 1 ? "match" : "matches"} {span}.
        </span>
      </div>
    </section>
  );
}
